import { ColDef, GridApi } from '@ag-grid-community/core'
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
} from '@angular/core'
import { FormControl, ValidatorFn } from '@angular/forms'
import {
  RootState,
  selectFocusedUIComponent,
  selectUserPolicy,
} from '@helios/state-management'
import {
  CreateNewFilterGroupModalService,
  FilterGroupService,
} from '@helios/user-preferences'
import { sortStringArray, toTitleCase } from '@helios/utils'
import { NgbActiveModal, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { Observable, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { AdvancedOptionsParams } from '../../models/advanced-options.model'
import { FilterEditorModel } from '../../models/filter-editor.model'
import { ColumnTypeOverrideService } from '../../services/column-type-override/column-type-override.service'
import { FilterGridRow, UpdatedFilter } from '../../models/filter-grid.model'
import { FilterModalEvent } from '../../models/filter-modal-events.model'
import { BuiltinFilterOperator } from '../../models/filter-operator.model'
import { FilterModel } from '../../models/filter.model'
import { getOperators } from '../../utils/filter-operator-utils'
import { getColumnType, getDataType } from '../../utils/filter-type-utils'
import { AdvancedOptionsComponent } from '../advanced-options/advanced-options.component'

type TabName = 'advancedOptions' | 'filtersGrid' | 'filtersForm'

@Component({
  selector: 'hs-filter-modal',
  templateUrl: './filter-modal.component.html',
  styleUrls: ['./filter-modal.component.scss'],
  providers: [NgbTooltipConfig],
})
export class FilterModalComponent implements OnInit, OnDestroy {
  @ViewChild('advancedOptionsComponent')
  readonly advancedOptionsComponent?: AdvancedOptionsComponent

  gridData: FilterGridRow[] = []
  activeTabId: TabName = 'filtersGrid'
  tabHotkeyBindings: { [key: string]: TabName } = {
    '1': 'filtersGrid',
    '2': 'advancedOptions',
  }
  gridKey = ''
  omitColumns: string[] = []
  advancedOptions?: Type<unknown>
  advancedOptionsParams?: AdvancedOptionsParams
  panelName = this.store.select(selectFocusedUIComponent)

  @Input() enableClearFilters = true
  @Input() enableResetFilters = false
  /**
   * Whether the filter editor may offer the per-column comparison-type picker (PVAL-26423). OFF by
   * default; set from {@link FilterModalService.columnTypeOverrideEnabled}, which only
   * `AggInfoFilterModalService` turns on. Purely a pass-through to `<hs-filter-editor>` — this
   * component makes no decision on it.
   */
  @Input() enableColumnTypeOverride = false
  @Input() validators: { [colId: string]: ValidatorFn } = {}

  _gridApi!: GridApi
  set gridApi(gridApi: GridApi) {
    this._gridApi = gridApi
    this.setTable()
  }

  editorModel: FilterEditorModel = {
    columnFilters: [],
    filterModel: {},
  }
  filterEditorToggle = new FormControl(true)

  /** @internal */
  readonly pendingChanges: UpdatedFilter[] = []

  private readonly destroy$ = new Subject<void>()

  readonly accessDeveloperTools$: Observable<boolean> = this.store
    .select(selectUserPolicy)
    .pipe(
      takeUntil(this.destroy$),
      map(userPolicy => userPolicy?.accessDeveloperTools ?? false),
    )

  constructor(
    public activeModal: NgbActiveModal,
    private readonly createNewFilterGroupModalService: CreateNewFilterGroupModalService,
    private readonly filterGroupService: FilterGroupService,
    private readonly tooltipConfig: NgbTooltipConfig,
    private readonly store: Store<RootState>,
    private readonly cd: ChangeDetectorRef,
    private readonly columnTypeOverrides: ColumnTypeOverrideService,
  ) {}

  ngOnInit(): void {
    this.tooltipConfig.openDelay = 0
    this.tooltipConfig.placement = 'bottom'
    this.tooltipConfig.tooltipClass = 'hs-tooltip'
    this.tooltipConfig.animation = false
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onApply() {
    const filterModel = this.filterEditorToggle.value
      ? this.editorModel.filterModel
      : this.convertPendingChangesToFilters()
    const event: FilterModalEvent = {
      type: 'Apply',
      filterModel: filterModel,
      payload: this.advancedOptionsParams,
    }

    this.commitPendingTypeOverrides()
    this.advancedOptionsComponent?.onApply()
    this.activeModal.close(event)
  }

  /**
   * Commit any AGGREGATE_INFO comparison-type picks made while this modal was open.
   *
   * `FilterEditorComponent.onColumnTypeChange` deliberately does not write to
   * {@link ColumnTypeOverrideService} itself — a picked type is an in-progress edit, same as a typed
   * value, and should discard on Cancel rather than rebuilding the grid's ColDefs (and resetting
   * whatever the user has since done in the Column Manager) before the user has committed anything.
   * `editorModel.columnFilters` already carries the CURRENT (possibly just-picked) type per column via
   * two-way binding, so Apply is simply where that gets written through.
   */
  private commitPendingTypeOverrides(): void {
    this.editorModel.columnFilters.forEach(column => {
      const typeOverride = column.typeOverride
      if (!typeOverride) return
      this.columnTypeOverrides.setType(
        typeOverride.scope,
        column.field,
        typeOverride.type,
      )
    })
  }

  onChange(value: UpdatedFilter) {
    const found = this.pendingChanges.find(x => x.column === value.column)
    if (found) {
      found.filterValue = value.filterValue
      found.operator = value.operator
    } else {
      this.pendingChanges.push(value)
    }

    const col = this.gridData.find(x => x.field === value.field)
    if (col) {
      col.filterValue = value.filterValue as string
    }
  }

  /** @internal */
  setTable() {
    const defs = this._gridApi.getColumnDefs()
    if (!defs) {
      return
    }

    const gridData: FilterGridRow[] = []

    for (const def of defs) {
      const colDef = def as ColDef

      // Check if not filterable
      if (!colDef.filter) {
        continue
      }

      // Check is column is omitted
      // Rows are applied by field, so a column known only by its colId can't be edited here.
      if (!colDef.field || this.omitColumns.includes(colDef.field)) {
        continue
      }

      let column = colDef.headerName
      if (!column) {
        column = toTitleCase(colDef.field ?? '')
      }

      if (!column) {
        continue
      }

      const filter = this._gridApi.getFilterModel()
      const existingFilter = filter[colDef.field ?? '']
      const filterValue = existingFilter?.filter ?? existingFilter?.values ?? ''
      let type = colDef.type as string

      if (typeof colDef.filter === 'string') {
        type = colDef.filter
      }

      const availableOperators = getOperators(colDef, this._gridApi) ?? []
      const defaultOperator =
        availableOperators.length > 0
          ? availableOperators[0]
          : BuiltinFilterOperator.equals

      let values: string[] | undefined = undefined
      if (Array.isArray(colDef.filterParams?.values)) {
        values = sortStringArray(colDef.filterParams.values)
      } else if (typeof colDef.filterParams?.values === 'function') {
        colDef.filterParams.values({
          success: (data: string[]) => {
            const field = this.gridData.find(x => x.field === colDef.field)
            if (field) {
              field.values = sortStringArray(data)
            } else {
              values = sortStringArray(data)
            }
            this.gridData = [...this.gridData]
          },
        })
      }

      gridData.push({
        field: colDef.field,
        column,
        filterValue,
        isVisible: true,
        operator: defaultOperator,
        availableOperators,
        columnType: type,
        editType: getDataType(type),
        values,
      })
    }

    // Assign at the end to avoid triggering a change event a million times
    this.gridData = gridData
  }

  @HostListener('document:keydown', ['$event'])
  onEscape(event: KeyboardEvent) {
    // Ignore when in input
    const input = event.target as HTMLElement

    if (
      input.tagName === 'INPUT' ||
      input.tagName === 'TEXTAREA' ||
      input.tagName === 'SELECT' ||
      input.classList.contains('ag-picker-field-wrapper')
    ) {
      return
    }

    if (event.key === 'Escape') {
      this.activeModal.dismiss('cancel')
    }

    const tabId = this.tabHotkeyBindings[event.key]
    if (tabId) {
      this.activeTabId = tabId
    }
  }

  /**
   * Convert the pending changes to filters
   */
  private convertPendingChangesToFilters(): FilterModel | undefined {
    const defs = this._gridApi.getColumnDefs() as ColDef[]
    if (!defs) {
      return
    }

    const filters = this._gridApi.getFilterModel()

    this.pendingChanges.forEach(change => {
      const def = defs.find(d => d.field === change.field && d.filter)
      if (!def) {
        return
      }

      const colType = getColumnType(def.type as string)
      if (colType === 'set' || def.filter === 'agSetColumnFilter') {
        const filterValue = this.convertToBooleanFilter(change.filterValue)

        const values = Array.isArray(filterValue) ? filterValue : [filterValue]

        filters[def.field ?? ''] = {
          values: values,
          filterType: 'set',
        }
      } else {
        filters[def.field ?? ''] = {
          filter: change.filterValue,
          filterTo: '',
          filterType: colType,
          type: change.operator?.type,
        }
      }
    })

    return filters
  }

  clearAllFilters() {
    const event: FilterModalEvent<null> = {
      type: 'ClearAll',
      payload: null,
    }

    this.activeModal.close(event)
  }

  resetAllFilters() {
    const event: FilterModalEvent<null> = {
      type: 'ResetAll',
      payload: null,
    }

    this.activeModal.close(event)
  }

  convertToBooleanFilter(filterValue: unknown | unknown[]): string[] {
    if (Array.isArray(filterValue)) {
      return filterValue.map(x => this.convertToBooleanFilter(x)[0])
    }

    if (filterValue === true) {
      return ['True']
    }

    if (filterValue === false) {
      return ['False']
    }

    return [filterValue as string]
  }
}
